import { Component, createRef } from 'react';
import sharedStyles from '../single-page.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import styles from './qr-code-generator.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

interface QrState {
    input: string;
    size: number;
    errorCorrection: 'L' | 'M' | 'Q' | 'H';
    fgColor: string;
    bgColor: string;
    copiedField: string | null;
    error: string;
}

export default class QrCodeGeneratorComponent extends Component<{ feature?: string }, QrState> {
    private canvasRef = createRef<HTMLCanvasElement>();
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    state: QrState = {
        input: 'https://xconvert.com',
        size: 256,
        errorCorrection: 'M',
        fgColor: '#000000',
        bgColor: '#ffffff',
        copiedField: null,
        error: '',
    };

    componentDidMount() {
        this.generateQR();
    }

    componentDidUpdate(_: any, prevState: QrState) {
        if (prevState.input !== this.state.input ||
            prevState.size !== this.state.size ||
            prevState.errorCorrection !== this.state.errorCorrection ||
            prevState.fgColor !== this.state.fgColor ||
            prevState.bgColor !== this.state.bgColor) {
            this.generateQR();
        }
    }

    async generateQR() {
        const { input, size, errorCorrection, fgColor, bgColor } = this.state;
        const canvas = this.canvasRef.current;
        if (!canvas || !input) return;
        try {
            // @ts-ignore
            const QRCode = await import('qrcode');
            await QRCode.toCanvas(canvas, input, {
                width: size,
                margin: 2,
                errorCorrectionLevel: errorCorrection,
                color: { dark: fgColor, light: bgColor },
            });
            this.setState({ error: '' });
        } catch (e: any) {
            this.setState({ error: e.message || 'Failed to generate QR code' });
        }
    }

    download = () => {
        const canvas = this.canvasRef.current;
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = 'qrcode.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    render() {
        const { input, size, errorCorrection, fgColor, bgColor, copiedField, error } = this.state;

        return (
            <FullscreenHost>
            <div className="row">
                <div className={`col-12 ${styles.wrapper}`}>
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Text / URL</label>
                        <textarea
                            className={sharedStyles.textarea}
                            placeholder="Enter text or URL to encode..."
                            value={input}
                            onChange={e => this.setState({ input: e.target.value })}
                            rows={3}
                        />
                        <div className={styles.options}>
                            <div className={styles.optionGroup}>
                                <label htmlFor="qr-size">Size: <span className={styles.sizeValue}>{size}px</span></label>
                                <input id="qr-size" type="range" min={128} max={512} step={8} value={size}
                                    className={styles.sizeSlider}
                                    onChange={e => this.setState({ size: Number(e.target.value) })} />
                            </div>
                            <div className={styles.optionGroup}>
                                <label htmlFor="qr-ec">Error Correction</label>
                                <select id="qr-ec" value={errorCorrection}
                                    onChange={e => this.setState({ errorCorrection: e.target.value as QrState['errorCorrection'] })}>
                                    <option value="L">L (Low ~7%)</option>
                                    <option value="M">M (Medium ~15%)</option>
                                    <option value="Q">Q (Quartile ~25%)</option>
                                    <option value="H">H (High ~30%)</option>
                                </select>
                            </div>
                            <div className={styles.optionGroup}>
                                <label htmlFor="qr-fg">Foreground</label>
                                <input id="qr-fg" type="color" value={fgColor}
                                    onChange={e => this.setState({ fgColor: e.target.value })} />
                            </div>
                            <div className={styles.optionGroup}>
                                <label htmlFor="qr-bg">Background</label>
                                <input id="qr-bg" type="color" value={bgColor}
                                    onChange={e => this.setState({ bgColor: e.target.value })} />
                            </div>
                        </div>
                        {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}
                    </div>
                    {input && (
                        <div className={genStyles.section}>
                            <div className={styles.canvasWrap}>
                                <canvas ref={this.canvasRef} />
                            </div>
                            <div className={`${sharedStyles.actions} ${styles.actions}`}>
                                <button className={sharedStyles.buttonPrimary} onClick={this.download}>
                                    ↓ Download PNG
                                </button>
                                <button
                                    className={`${sharedStyles.copyButton} ${copiedField === 'input' ? sharedStyles.copyButtonSuccess : ''}`}
                                    onClick={() => {
                                        navigator.clipboard.writeText(input).then(() => {
                                            if (this.copyTimeout) clearTimeout(this.copyTimeout);
                                            this.setState({ copiedField: 'input' });
                                            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
                                        });
                                    }}
                                >
                                    {copiedField === 'input' ? '✓ Copied' : 'Copy Input'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            </FullscreenHost>
        );
    }
}
